import DetailViewModel from "../../../../detailViewModel";
import type { EntityNames } from "config";
import type { interfaces } from "inversify";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import EnumService from "services/enumService";
import { computed } from "mobx";
import PriceListService from "services/priceListService";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import { emptyValue } from "utils/emptyValue";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import VehicleRegisterVehicleDetailResponse from "entities/vehicleRegisterVehicleDetailResponse";

export default class VehicleRegisterDetailInfoViewModel extends DetailViewModel<VehicleRegisterVehicleDetailResponse> {
  protected readonly modelName: EntityNames = "vehicleRegisterDetail";

  constructor(
    protected itemId: string,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected repository: VehicleRegisterRepository
  ) {
    super(itemId);
    this.navigationName = "";
  }

  protected loadDetail(): Promise<VehicleRegisterVehicleDetailResponse> | VehicleRegisterVehicleDetailResponse {
    return this.repository.getOne(this.itemId);
  }

  @computed
  get name(): string {
    return this.translate("name");
  }

  @computed
  get sectionName(): string {
    return this.translate("sectionName");
  }

  @computed
  get vehicleRegisterDetail(): VehicleRegisterVehicleDetailResponse {
    return this.item;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleRegisterDetailInfo.${code}`, size);
  }

  translateSubAttribute(model: string, attribute: string): string {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: VehicleRegisterVehicleFuelType[]): string {
    const translation = fuelType.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType): string {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleRegisterSynchronizationStatus(status: VehicleRegisterSynchronizationStatus): string {
    return this.localizationService.translateEnum("vehicleRegisterSynchronizationStatus", status);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus): string {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateCountryId(countryId: string): string {
    return this.enumService.value("countries", countryId)?.name ?? emptyValue;
  }

  formatHeight(amount: number): string {
    return `${amount} kg`;
  }

  static Factory({ container }: interfaces.Context) {
    return (vehicleRegister: VehicleRegisterVehicle) =>
      new VehicleRegisterDetailInfoViewModel(
        vehicleRegister.vehicleId,
        container.get(LocalizationService),
        container.get(PriceListService),
        container.get(EnumService),
        container.get(VehicleRegisterRepository)
      );
  }
}
